import { useAllUsersOrgs } from '@weenat/client/dist/hooks/useAllUsersOrgs'
import { PlotOrStationSummary } from '@weenat/client/dist/resources/measurements'
import { Summary } from '@weenat/client/dist/resources/measurements.type'
import { useIntl } from '@weenat/wintl'
import { useOrgContext } from 'app/orgProvider'
import Card from 'app/src/kit/Card'
import Icons from 'app/src/kit/Icons'
import Text from 'app/src/kit/Text'
import ViewKindSelector from 'app/src/kit/ViewKindSelector'
import MarkerLegendModal from 'app/src/map/MarkerLegendModal'
import DisplaySettings from 'app/src/myFarm/tools/DisplaySettings'
import MyFarmTools from 'app/src/myFarm/tools/MyFarmTools'
import { useToken } from 'app/state'
import { isEmpty } from 'lodash-es'
import isNil from 'lodash-es/isNil'
import { memo, useCallback } from 'react'
import Geolocation from '../../../map/Geolocation'
import ControlsContainer from './ControlsContainer'
import DashboardMapLoading from './DashboardMapLoading'
import MapControlsOverlay from './MapControlsOverlay'
import MapOverlay from './MapOverlay'
import Radar from './Radar/Radar'
import useIsRadarEnabled from './Radar/hooks/useIsRadarEnabled'
import ZoomButtons from './ZoomButtons'
import { elementIds } from './constants'
import {
  useBackgroundMapContext,
  useBackgroundMapDispatcher
} from './contexts/BackgroundMapContext'
import useDrawVisiblePlotShapes from './hooks/useDrawVisiblePlotShapes'
import useUniverseOptionsQuery from './universes/useUniverseOptionsQuery'

type PlotsMapControlsProps = {
  summaries: Summary[]
  filteredSummaries: Summary[]
}

const PlotsMapControls: FC<PlotsMapControlsProps> = memo(({ summaries, filteredSummaries }) => {
  const { t } = useIntl()
  const [token] = useToken()
  const org = useOrgContext()

  const { universeOptions } = useUniverseOptionsQuery(org.id)
  const { farmsOptions } = useAllUsersOrgs({ enabled: !isNil(token) })

  const hasOnlyOneFarm = farmsOptions.length <= 1

  const {
    api: { map, maps }
  } = useBackgroundMapContext()
  const dispatch = useBackgroundMapDispatcher()

  const [isRadarEnabled] = useIsRadarEnabled()

  const onGeolocationSuccess = useCallback<PositionCallback>(
    ({ coords: { latitude, longitude } }) => {
      dispatch({
        type: 'setGeolocation',
        newGeolocation: {
          lat: latitude,
          lng: longitude
        }
      })
    },
    [dispatch]
  )

  return (
    <MapControlsOverlay>
      {!isRadarEnabled ? (
        <Flex $flex={1} $flexDirection='column' $justifyContent='space-between'>
          {/* TOP */}
          <MyFarmTools
            summaries={summaries}
            filteredResults={filteredSummaries}
            displaySettingsElement={<DisplaySettings isWeatherMap={false} />}
            isPlotsMap
          />

          {/* FILLS THE SCREEN */}
          <Flex $flex={1} $flexDirection='column' $width='100%' $gap='xl'>
            {isEmpty(summaries) && isEmpty(universeOptions) ? (
              <Card $m={'auto'} $gap={'lg'} $width={512} $maxHeight={412}>
                <Text $fontWeight='bold' $textAlign='center' $fontSize='md'>
                  {t('help.empty_map.welcome')}
                </Text>

                <Flex $alignItems='center' $gap='md' $flexDirection='column'>
                  <Text $textAlign='center' $lineHeight='md'>
                    {t('help.empty_map.my_farm_explained')}
                  </Text>

                  <Text $textAlign='center' $lineHeight='md'>
                    {t('help.empty_map.to_add_use_button')}
                    <Box
                      style={{
                        display: 'inline-block',
                        marginInlineStart: 4,
                        marginInlineEnd: 4
                      }}
                    >
                      <Icons.PlusSignCircle
                        $size='md'
                        $p={'sm'}
                        $color={'grayscale.white'}
                        $backgroundColor={'primary.500'}
                      />
                    </Box>
                    {t('help.empty_map.inside_left_pannel')}
                  </Text>

                  {hasOnlyOneFarm ? null : (
                    <Text $textAlign='center'>{t('help.empty_map.to_switch_use_select')}</Text>
                  )}
                </Flex>
              </Card>
            ) : (
              <>
                {/* SMASHED RIGHT */}
                <Flex
                  $flexDirection='column'
                  $gap='lg'
                  $justifyContent='center'
                  $alignItems='center'
                  $alignSelf='flex-end'
                  $flex={1}
                  $mt={80}
                >
                  <Flex $flexDirection='column' $gap='md'>
                    <ControlsContainer $flexDirection='column-reverse' $py='sm' $gap='sm'>
                      <ZoomButtons />
                    </ControlsContainer>

                    <ControlsContainer>
                      <Geolocation onSuccess={onGeolocationSuccess} tooltipPlacement='left' />
                    </ControlsContainer>

                    <Radar map={map} maps={maps} />

                    <ControlsContainer>
                      <MarkerLegendModal tooltipPlacement='left' />
                    </ControlsContainer>
                  </Flex>
                </Flex>

                {/* BOTTOM */}
                <ViewKindSelector />
              </>
            )}
          </Flex>
        </Flex>
      ) : (
        <Radar map={map} maps={maps} />
      )}
    </MapControlsOverlay>
  )
})

interface PlotsMapOverlayProps {
  currentItems: PlotOrStationSummary[]
  isLoading: boolean
  allSummaries: PlotOrStationSummary[]
}

const PlotsMapOverlay: FC<PlotsMapOverlayProps> = ({
  allSummaries: summaries,
  children,
  currentItems,
  isLoading
}) => {
  const org = useOrgContext()

  const {
    api: { map, maps }
  } = useBackgroundMapContext()

  const shouldShowControls = !isNil(map) && !isNil(maps)

  useDrawVisiblePlotShapes({ currentOrganizationId: org.id, summaries: currentItems })
  return (
    <MapOverlay id={elementIds.OverlayContainer} $flexDirection={'column'}>
      {isLoading ? (
        <DashboardMapLoading />
      ) : (
        <>
          {shouldShowControls ? (
            <PlotsMapControls
              // all summaries not only current
              summaries={summaries}
              filteredSummaries={currentItems}
            />
          ) : null}
          {children}
        </>
      )}
    </MapOverlay>
  )
}

export default PlotsMapOverlay
